import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { devWarn } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [modulesAccess, setModulesAccess] = useState([]);
  const [perms, setPerms] = useState({}); // { moduleKey: {read, create, update, delete} }
  const [loading, setLoading] = useState(true);

  // `api` is a stable module import; setters are stable per React contract, so
  // this callback has no reactive dependencies.
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setModulesAccess(data.modules_access || []);
      setPerms(data.permissions || {});
    } catch (err) {
      devWarn("Auth me failed", err?.response?.status);
      setUser(null);
      setModulesAccess([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      // fetch modules access + per-module CRUD permissions
      const me = await api.get("/auth/me");
      setModulesAccess(me.data.modules_access || []);
      setPerms(me.data.permissions || {});
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { devWarn("Logout call failed", err); }
    setUser(null);
    setModulesAccess([]);
    setPerms({});
  };

  return (
    <AuthContext.Provider value={{ user, modulesAccess, perms, loading, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
